/**
 * The Spotlight — the board's one computed pointer, and its one real algorithm.
 *
 * Precedence (CONTEXT.md "Spotlight", nudge-mechanics ticket 02):
 *
 *   0. compose hostage — until today's Day plan is composed, the Spotlight *is*
 *      "Compose today." The board refuses to guess a day Kevin hasn't picked.
 *   1. a live calendar event (hard commitment, happening right now)
 *   2. the top unchecked Day-plan item in morning-pick order — never re-sorted
 *      by rank mid-day (rank had its influence at compose time)
 *   3. the next action of the highest-ranked Direction's active Goal
 *   4. an overdue Review
 *   5. never empty: the highest-ranked Direction itself
 *
 * Two ordering calls this file had to make (both recorded as open questions on
 * the build ticket):
 *   - the hostage sits ABOVE the live calendar event (ticket 02: "the
 *     spotlight *is* 'Compose today'"; prototype H renders the morning state
 *     full-screen). The live event is not hidden — it still shows in the
 *     then-line.
 *   - next action before overdue review, per CONTEXT.md's enumeration
 *     (ticket 02's prose reads as though an overdue review sits right behind
 *     the plan items).
 *
 * Mode does not change this chain: Mode-on strips what the board *renders*,
 * not what the Spotlight *is* (the confrontation freeze in ticket 02 covers
 * erosion flips and due chips — see `render/strip.ts`).
 */

import type {
  CalendarEvent,
  DayPlan,
  DefsDirection,
  DefsGoal,
  ReviewState,
} from './types';

export type SpotlightSource =
  | 'compose'
  | 'calendar'
  | 'planItem'
  | 'nextAction'
  | 'review'
  | 'direction'
  | 'empty';

export interface Spotlight {
  source: SpotlightSource;
  /** small uppercase line above the item. */
  kicker: string;
  /** the enlarged item itself. */
  what: string;
  /** the "serves →" line. */
  serves: string;
  /** id of the thing the spotlight points at, when it has one. */
  refId?: string;
}

export interface SpotlightInput {
  /** server-local today, YYYY-MM-DD. */
  today: string;
  /** the instant to evaluate "live" against. */
  now: Date;
  /** today's plan, or null = never composed today. */
  plan: DayPlan | null;
  events: CalendarEvent[];
  /** sorted by rank ascending, but this module does not rely on that. */
  directions: DefsDirection[];
  goals: DefsGoal[];
  reviews: { weekly: ReviewState; quarterly: ReviewState } | null;
  /** shelf sizes, for the hostage sub-line. */
  shelfCounts?: { events: number; due: number; nextActions: number; habits: number };
}

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

const byId = <T extends { id: string }>(xs: T[]): Map<string, T> =>
  new Map(xs.map((x) => [x.id, x]));

/** The calendar event happening right now, if any (earliest start wins). */
export function liveEvent(events: CalendarEvent[], now: Date): CalendarEvent | null {
  const t = now.getTime();
  const live = events
    .filter((e) => !e.allDay)
    .filter((e) => {
      const s = Date.parse(e.start);
      const end = Date.parse(e.end);
      return Number.isFinite(s) && Number.isFinite(end) && s <= t && t < end;
    })
    .sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
  return live[0] ?? null;
}

/** The next calendar event after `now` — the then-line, not the Spotlight. */
export function nextEvent(events: CalendarEvent[], now: Date): CalendarEvent | null {
  const t = now.getTime();
  const upcoming = events
    .filter((e) => !e.allDay && Date.parse(e.start) > t)
    .sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
  return upcoming[0] ?? null;
}

/**
 * The active Goal that owns the fallback slot: walk Directions by rank
 * ascending, take the first one with an active Goal carrying a next action.
 * Within a Direction, the soonest `due` wins (id breaks ties) — deterministic,
 * because "which of a Direction's active Goals" is not decided anywhere.
 */
export function rankFallbackGoal(
  directions: DefsDirection[],
  goals: DefsGoal[],
): { goal: DefsGoal; direction: DefsDirection } | null {
  const ranked = [...directions].sort((a, b) => a.rank - b.rank);
  for (const direction of ranked) {
    const candidates = goals
      .filter(
        (g) =>
          g.directionId === direction.id &&
          g.status === 'active' &&
          !!g.nextAction &&
          g.nextAction.trim() !== '',
      )
      .sort((a, b) => (a.due === b.due ? a.id.localeCompare(b.id) : a.due.localeCompare(b.due)));
    if (candidates[0]) return { goal: candidates[0], direction };
  }
  return null;
}

/** The overdue Review that claims the fallback slot; weekly outranks quarterly. */
export function overdueReview(
  reviews: { weekly: ReviewState; quarterly: ReviewState } | null,
): { kind: 'weekly' | 'quarterly'; review: ReviewState } | null {
  if (!reviews) return null;
  if (reviews.weekly?.overdue) return { kind: 'weekly', review: reviews.weekly };
  if (reviews.quarterly?.overdue) return { kind: 'quarterly', review: reviews.quarterly };
  return null;
}

export function computeSpotlight(input: SpotlightInput): Spotlight {
  const { plan, events, now, directions, goals, reviews } = input;
  const dirs = byId(directions);
  const servesDirection = (id: string | null | undefined): string => {
    if (!id) return '';
    const d = dirs.get(id);
    return d ? `serves → ${d.heading}` : '';
  };

  // 0. compose hostage — the daily review holds the most valuable pixel.
  if (!plan) {
    const c = input.shelfCounts;
    return {
      source: 'compose',
      kicker: 'Daily review',
      what: 'Compose today.',
      serves: c
        ? `The board doesn't know your day until you pick it · shelf: ${plural(c.events, 'event')} · ${c.due} due · ${plural(c.nextActions, 'next action')} · ${plural(c.habits, 'habit')}`
        : "The board doesn't know your day until you pick it.",
    };
  }

  // 1. a live calendar event — a hard commitment beats anything picked.
  const live = liveEvent(events, now);
  if (live) {
    return {
      source: 'calendar',
      kicker: 'Now — calendar',
      what: live.title,
      serves: 'hard commitment',
      refId: live.id,
    };
  }

  // 2. the top unchecked Day-plan item, in morning-pick order.
  const nextItem = [...plan.items]
    .sort((a, b) => a.position - b.position)
    .find((i) => i.checkedAt === null);
  if (nextItem) {
    return {
      source: 'planItem',
      kicker: 'Next right thing',
      what: nextItem.label,
      serves: servesDirection(nextItem.directionId) || 'picked this morning',
      refId: nextItem.id,
    };
  }

  // 3. plan exhausted — the highest-ranked Direction's active Goal.
  const fallback = rankFallbackGoal(directions, goals);
  if (fallback) {
    return {
      source: 'nextAction',
      kicker: 'Plan done — highest direction',
      what: fallback.goal.nextAction as string,
      serves: `serves → ${fallback.direction.heading} · ${fallback.goal.outcome}`,
      refId: fallback.goal.id,
    };
  }

  // 4. an overdue Review.
  const overdue = overdueReview(reviews);
  if (overdue) {
    return {
      source: 'review',
      kicker: 'Overdue review',
      what: overdue.kind === 'weekly' ? 'Run the weekly review.' : 'Run the quarterly review.',
      serves: `was due ${overdue.review.dueOn} · ${overdue.review.period}`,
      refId: overdue.kind,
    };
  }

  // 5. never empty.
  const top = [...directions].sort((a, b) => a.rank - b.rank)[0];
  if (top) {
    return {
      source: 'direction',
      kicker: 'Nothing queued — highest direction',
      what: top.heading,
      serves: top.why,
      refId: top.id,
    };
  }
  return {
    source: 'empty',
    kicker: 'Cascade empty',
    what: 'No Directions defined.',
    serves: 'add one in defs/directions/',
  };
}
